/**
 * Organization data builder utilities
 * Constructs organization mutation data objects
 */

import { Bank } from "../../../types/org.type";
import { DocumentsUpload } from "@funded-here-interface/common/src/constant/type";
import { KYCStatus } from "@funded-here-interface/common/src/constant/enum";

export interface OrgBizState {
  company: string | null;
  regNumber: string | null;
  dateOfIncorporation: string | null;
  legalStructure: string | null;
  address: string | null;
  postal: string | null;
  website: string | null;
  industryGroup: string | null;
  typeOfInstitute: string | null;
  userFirstName: string | null;
  userLastName: string | null;
  countryISOCode?: string | null;
}

export interface OrgBankState {
  bankName: string | null;
  swiftCode: string | null;
  accountNumber: string | null;
  accountHolderName: string | null;
  accountType: string | null;
  bankAddress: string | null;
  bankCountry: string | null;
  currency: string | null;
}

export interface BaseOrgData {
  bank: Bank;
  token: string;
  onBoardingUserType: string;
  legalEntityAddress: string;
  countryISOCode: string | null;
  postalCode: string;
  documentsUpload: DocumentsUpload;
  companyName: string;
  companyRegistrationNo: string;
  companyStartDate?: string | null;
  industryGroup: string;
  legalStructure: string;
  firstName: string;
  lastName: string;
  industry: string;
  website?: string;
}

export interface CreateOrgData extends BaseOrgData {}

export interface UpdateOrgData extends BaseOrgData {
  kycStatus: KYCStatus;
}

/**
 * Build bank object from bank state
 */
export const buildBankObject = (bankState: OrgBankState): Bank => {
  return {
    bankName: bankState.bankName || "",
    swiftCode: bankState.swiftCode || "",
    accountNo: bankState.accountNumber || "",
    accountHolderName: bankState.accountHolderName || "",
    accountType: (bankState.accountType || "").toLowerCase(),
    bankAddress: bankState.bankAddress || "",
    bankCountry: bankState.bankCountry || "",
    currency: bankState.currency || "",
  };
};

/**
 * Build base organization data object
 * Used by both create and update operations
 */
export const buildBaseOrgData = (params: {
  bizState: OrgBizState;
  bank: Bank;
  token: string;
  onBoardingUserType: string;
  countryCode: string | null;
  documentsUpload: DocumentsUpload;
}): BaseOrgData => {
  const {
    bizState,
    bank,
    token,
    onBoardingUserType,
    countryCode,
    documentsUpload,
  } = params;

  return {
    bank,
    token,
    onBoardingUserType,
    legalEntityAddress: bizState.address as string,
    countryISOCode: countryCode,
    postalCode: `${bizState.postal}`,
    documentsUpload,
    companyName: bizState.company as string,
    companyRegistrationNo: bizState.regNumber as string,
    companyStartDate: bizState.dateOfIncorporation,
    industryGroup: bizState.industryGroup as string,
    legalStructure: bizState.legalStructure as string,
    firstName: bizState.userFirstName as string,
    lastName: bizState.userLastName as string,
    industry: bizState.typeOfInstitute as string,
    website: bizState.website || undefined,
  };
};

/**
 * Build create organization data
 */
export const buildCreateOrgData = (params: {
  bizState: OrgBizState;
  bank: Bank;
  token: string;
  onBoardingUserType: string;
  countryCode: string | null;
  documentsUpload: DocumentsUpload;
}): CreateOrgData => {
  return buildBaseOrgData(params);
};

/**
 * Build update organization data (includes kycStatus)
 */
export const buildUpdateOrgData = (params: {
  bizState: OrgBizState;
  bank: Bank;
  token: string;
  onBoardingUserType: string;
  countryCode: string | null;
  documentsUpload: DocumentsUpload;
}): UpdateOrgData => {
  return {
    ...buildBaseOrgData(params),
    kycStatus: KYCStatus.PENDING_APPROVAL,
  };
};
